import { useState } from 'react';
import { Share2, X } from 'lucide-react';
import type { SessionSharingPolicy, User } from '@reflex/shared';
import { Button } from '@reflex/ui/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import type { OwnerSelectTeamOption } from '@reflex/ui/components/ownership/OwnerSelect';
import { SharePicker, type ShareSelection } from '@/components/sharing/SharePicker';
import { CountBadge } from '@/components/shared/CountBadge';

/**
 * A share the user picked before launch. The agent does not exist yet, so no
 * grant is created until the launch succeeds — the dialog holds these and
 * posts one resource grant per share with the new agent id.
 */
export type PendingShare = ShareSelection;

interface LaunchShareMenuProps {
  value: PendingShare[];
  onChange: (value: PendingShare[]) => void;
  /** Teams the current user belongs to. */
  teams: OwnerSelectTeamOption[];
  /** Org members offered for a direct share (caller excludes the current user). */
  users: User[];
  /** The active organization id — the required `subjectId` for an org-wide share. */
  orgId: string;
  /** Org display name for the general-access row. */
  orgName?: string | null;
  /** The org's ceiling on what a share may confer; see {@link SharePicker}. */
  policy: SessionSharingPolicy;
  disabled?: boolean;
}

/**
 * "Share" affordance for the launch dialog footer. Opens the {@link SharePicker}
 * to build the list of people, teams, or the whole org the session will be
 * shared with. Selections are reported through `onChange` as pending shares and
 * summarized as a count chip; nothing is posted here.
 */
export function LaunchShareMenu({
  value,
  onChange,
  teams,
  users,
  orgId,
  orgName,
  policy,
  disabled,
}: LaunchShareMenuProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex min-w-0 items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {value.length > 0 ? (
            <button
              type="button"
              disabled={disabled}
              aria-label={`Share, ${value.length} selected`}
              data-testid="launch-share-chip"
              className="flex min-w-0 items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-2xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <Share2 className="icon-xs shrink-0" />
              <span className="truncate">Share</span>
              <CountBadge count={value.length} data-testid="launch-share-count" />
            </button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              aria-label="Share session"
              disabled={disabled}
            >
              <Share2 className="icon-xs" />
              Share
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end" data-testid="launch-share-menu">
          <SharePicker
            value={value}
            onChange={onChange}
            teams={teams}
            users={users}
            orgId={orgId}
            orgName={orgName}
            policy={policy}
          />
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label="Clear shares"
          disabled={disabled}
          onClick={() => onChange([])}
        >
          <X className="icon-xs" />
        </Button>
      )}
    </div>
  );
}
